// "My Queue" — orders awaiting the logged-in user's role action (D4 from
// Kalpana UAT round 3). Admin sees orders in dispatch_admin team; User sees
// orders in dispatch_user team. Each row is flagged unread (no handoff out
// of the team yet) or read.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { orderApi } from '../api/services';
import LifecycleBadge from '../components/common/LifecycleBadge';
import OrderIdCell from '../components/common/OrderIdCell';
import toast from 'react-hot-toast';
import { format, formatDistanceToNow } from 'date-fns';
import { Inbox, Loader2, Mail, MailOpen, Pause, Bell } from 'lucide-react';

export default function MyQueue() {
  const [team, setTeam] = useState(null);
  const [orders, setOrders] = useState([]);
  const [unread, setUnread] = useState(0);
  const [holds, setHolds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('unread');

  useEffect(() => {
    setLoading(true);
    orderApi.myQueue()
      .then((res) => {
        setTeam(res.data.team);
        setOrders(res.data.orders || []);
        setUnread(res.data.unread_count || 0);
        setHolds(res.data.holds || []);
      })
      .catch((err) => toast.error(err.response?.data?.error || 'Failed to load queue'))
      .finally(() => setLoading(false));
  }, []);

  async function pingHold(id) {
    const note = window.prompt('Optional note (or leave empty):', '');
    if (note === null) return;
    try {
      await orderApi.remind(id, note || null);
      toast.success('Reminder sent to Admin and Ops');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send reminder');
    }
  }

  if (loading) {
    return <div className="p-6 text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading queue…</div>;
  }
  if (!team) {
    return (
      <div className="p-6 max-w-3xl mx-auto bg-white rounded-xl border border-gray-200 text-sm text-gray-500 text-center">
        No queue assigned to your role. This page is for Dispatch Admin and Dispatch User.
      </div>
    );
  }

  const visible = tab === 'unread'
    ? orders.filter((o) => o.is_unread)
    : tab === 'read'
      ? orders.filter((o) => !o.is_unread)
      : orders;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <p className="text-sm text-gray-500 mb-4">
        Orders currently waiting on your action. <strong>Unread</strong> means nobody from your
        team has acted on the order yet since it landed in your queue. The count here is the
        same number you see in the sidebar.
      </p>

      <div className="border-b border-gray-200 mb-4 flex gap-4">
        {[
          { key: 'unread', label: `Unread (${orders.filter((o) => o.is_unread).length})` },
          { key: 'read',   label: `Touched (${orders.filter((o) => !o.is_unread).length})` },
          { key: 'all',    label: `All (${orders.length})` },
          { key: 'holds',  label: `Holds (${holds.length})` },
        ].map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
                  className={`px-1 py-2 text-sm font-medium border-b-2 -mb-px ${
                    tab === t.key
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'holds' ? (
        holds.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-sm text-gray-500">
            <Pause className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            No orders on hold.
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="text-left px-3 py-2">Order</th>
                  <th className="text-left px-3 py-2">Customer</th>
                  <th className="text-left px-3 py-2">Hold by</th>
                  <th className="text-left px-3 py-2">Hold reason</th>
                  <th className="text-left px-3 py-2">Last update</th>
                  <th className="text-right px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {holds.map((o) => (
                  <tr key={o.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-mono text-xs">
                      <Link to={`/orders/${o.id}`} className="text-blue-600 hover:underline">
                        <OrderIdCell order={o} />
                      </Link>
                    </td>
                    <td className="px-3 py-2">{o.customer_name}</td>
                    <td className="px-3 py-2 text-xs">
                      {o.hold_by
                        ? <span><span className="capitalize">{o.hold_by.team || 'system'}</span>
                            {(o.hold_by.user_name || o.hold_by.user_email) && (
                              <span className="text-gray-500"> · {o.hold_by.user_name || o.hold_by.user_email}</span>
                            )}
                          </span>
                        : '—'}
                    </td>
                    <td className="px-3 py-2 text-xs">{o.hold_reason_label || o.current_owner_reason_code || '—'}</td>
                    <td className="px-3 py-2 text-xs text-gray-500">
                      {o.current_owner_since
                        ? formatDistanceToNow(new Date(o.current_owner_since), { addSuffix: true })
                        : '—'}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => pingHold(o.id)}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-lg bg-amber-100 text-amber-900 hover:bg-amber-200">
                        <Bell className="w-3 h-3" /> Send reminder
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : visible.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-sm text-gray-500">
          <Inbox className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          Nothing in this view.
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left px-3 py-2 w-8"></th>
                <th className="text-left px-3 py-2">Order</th>
                <th className="text-left px-3 py-2">Customer</th>
                <th className="text-left px-3 py-2">Stage</th>
                <th className="text-left px-3 py-2">From file</th>
                <th className="text-left px-3 py-2">In your queue</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {visible.map((o) => (
                <tr key={o.id} className={`hover:bg-gray-50 ${o.is_unread ? 'font-semibold' : ''}`}>
                  <td className="px-3 py-2">
                    {o.is_unread
                      ? <Mail className="w-4 h-4 text-blue-600" title="Unread" />
                      : <MailOpen className="w-4 h-4 text-gray-300" title="Touched" />}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">
                    <Link to={`/orders/${o.id}`} className="text-blue-600 hover:underline">
                      <OrderIdCell order={o} />
                    </Link>
                  </td>
                  <td className="px-3 py-2">{o.customer_name}</td>
                  <td className="px-3 py-2">
                    <LifecycleBadge stage={o.lifecycle_stage} ownerTeam={o.current_owner_team} hasReship={o.has_reship} />
                  </td>
                  <td className="px-3 py-2 text-[11px] text-gray-600 max-w-[200px] truncate"
                      title={o.intake_filename || ''}>
                    {o.intake_filename || '—'}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">
                    {o.current_owner_since
                      ? formatDistanceToNow(new Date(o.current_owner_since), { addSuffix: true })
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
